import type { ChoiceOption, CollectInputField, CollectInputFieldValue, EditorSide, EditorTile } from "@common/schemas/icertis"

type FieldValues = Record<string, CollectInputFieldValue>
type EditorSidesField = Extract<CollectInputField, { type: "editor_sides" }>
type ResolvedOptions = Record<string, ChoiceOption[]>

export interface FieldsAndValuesForThePickedParty {
  fields: CollectInputField[]
  values: FieldValues
}

const SIDE_FOR_THE_OTHER_PARTY: Record<EditorSide, EditorSide> = { our_side: "counterparty", counterparty: "our_side", unplaced: "unplaced" }

function editorsForTheOtherParty(editors: EditorTile[]): EditorTile[] {
  return editors.map((editor) => ({ ...editor, side: SIDE_FOR_THE_OTHER_PARTY[editor.side] }))
}

function pickedPartyLabel(
  partyField: CollectInputField | undefined,
  value: CollectInputFieldValue | undefined,
  resolvedOptions: ResolvedOptions | undefined,
): string | null {
  if (partyField?.type !== "single_select" || value?.type !== "select") return null
  const [pickedIndex] = value.selected
  const options = partyField.options?.length ? partyField.options : resolvedOptions?.[partyField.id] ?? []
  return options[pickedIndex]?.label ?? value.selectedLabels?.[0] ?? null
}

export function actsForTheCounterparty(
  field: EditorSidesField,
  fields: CollectInputField[],
  values: FieldValues,
  resolvedOptions?: ResolvedOptions,
): boolean {
  const partyFieldId = field.party_field_id
  if (!partyFieldId) return false
  const partyField = fields.find((candidate) => candidate.id === partyFieldId)
  return pickedPartyLabel(partyField, values[partyFieldId], resolvedOptions) === field.counterparty_label
}

function editorSidesFieldForTheOtherParty(field: EditorSidesField): EditorSidesField {
  return {
    ...field,
    our_side_label: field.counterparty_label,
    counterparty_label: field.our_side_label,
    editors: editorsForTheOtherParty(field.editors),
  }
}

export function fieldsAndValuesForThePickedParty(
  fields: CollectInputField[],
  values: FieldValues,
  resolvedOptions?: ResolvedOptions,
): FieldsAndValuesForThePickedParty {
  const pickedFields = fields.map((field) =>
    field.type === "editor_sides" && actsForTheCounterparty(field, fields, values, resolvedOptions)
      ? editorSidesFieldForTheOtherParty(field)
      : field,
  )
  const pickedValues = { ...values }
  for (const field of fields) {
    if (field.type !== "editor_sides" || !actsForTheCounterparty(field, fields, values, resolvedOptions)) continue
    const stored = values[field.id]
    pickedValues[field.id] = { type: "editor_sides", editors: editorsForTheOtherParty(stored?.type === "editor_sides" ? stored.editors : field.editors) }
  }
  return { fields: pickedFields, values: pickedValues }
}
